import { useRef, useState } from "react";
import { DesignerUploadRevisionService } from "shared/services/revision";
import { ProjectCompleteService } from "shared/services/project";
import { useRevisionUpload } from "shared/hooks/useRevisionUpload";
import { FileUploadSection } from "shared/components/designer/uploadRevision/fileUpload";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { UploadSortValue } from "shared/components/common/customUpload";
import CustomModal from "shared/components/common/customModal";
import CustomInput from "shared/components/common/customInput";
import CustomTextEditor from "shared/components/common/customTextEditor";
import CustomCheckbox from "shared/components/common/customCheckbox";
import { isEmptyRichText } from "./isEmptyRichText";
import classNames from "classnames";
import styles from "./style.module.scss";
import { FINAL_PLANS_DROPZONE_ACCEPT } from "shared/constants/officeFiles";

const WARNING_TEXT =
  "You are submitting the final version of the plans to the client and will no longer be able to submit further revisions. Please confirm with the billing department that the final payment has been made prior to sending the final plans without preliminary stamps.";

interface FinalPlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  projectName?: string;
  onSuccess?: () => void;
}

const FinalPlansModal = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  onSuccess,
}: FinalPlansModalProps) => {
  const [title, setTitle] = useState("");
  const [designerNote, setDesignerNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [mediaSort, setMediaSort] = useState<UploadSortValue | null>(null);
  // Set once the final-plans revision is created, so a retry after a failed
  // complete call doesn't try to create it twice (server rejects duplicates).
  const finalPlansCreatedRef = useRef(false);

  const {
    uploadedFileIds,
    uploadingFiles,
    uploadProgress,
    currentFiles,
    fileIdMap,
    processingFilesRef,
    getFileKey,
    handleFilesSelected,
    setUploadedFileIds,
    setCurrentFiles,
    setFileIdMap,
    resetUploadState,
  } = useRevisionUpload();

  /** Cancel discards the draft, so reopening starts clean rather than mid-edit. */
  const resetForm = () => {
    setTitle("");
    setDesignerNote("");
    setInternalNote("");
    setIsChecked(false);
  };

  /** Uploads left behind here are swept server-side 24h later, so closing only drops the draft. */
  const handleClose = () => {
    if (submitting) return;

    resetUploadState();
    setShowUploadSuccess(false);
    setMediaSort(null);
    finalPlansCreatedRef.current = false;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toastMessage("error", "Please enter a revision title");
      return;
    }
    if (uploadedFileIds.length === 0 || !showUploadSuccess) {
      toastMessage("error", "Please upload the final plans first");
      return;
    }
    if (!isChecked) {
      toastMessage(
        "error",
        "Please confirm you have read the final plans notice",
      );
      return;
    }

    try {
      setSubmitting(true);

      if (!finalPlansCreatedRef.current) {
        try {
          const uploadResponse = await DesignerUploadRevisionService({
            title: title.trim(),
            designer_comment: isEmptyRichText(designerNote) ? "" : designerNote,
            internal_comment: isEmptyRichText(internalNote) ? "" : internalNote,
            project_id: projectId,
            media_sort: mediaSort,
            is_final_plans: true,
            designer_uploads: uploadedFileIds.map((id, index) => ({
              id: id,
              order: index + 1,
            })),
          });
          if (uploadResponse.status !== 200 && uploadResponse.status !== 201) {
            handleErrors(uploadResponse);
            return;
          }
        } catch (error: any) {
          // 409 = final plans were already submitted (e.g. an earlier attempt
          // died before completing) — safe to proceed straight to completion.
          if (error?.response?.status !== 409) {
            throw error;
          }
        }
        finalPlansCreatedRef.current = true;
      }

      const completeResponse = await ProjectCompleteService(projectId);
      if (completeResponse.status === 200 || completeResponse.status === 201) {
        resetUploadState();
        setShowUploadSuccess(false);
        resetForm();
        finalPlansCreatedRef.current = false;
        onSuccess?.();
      } else {
        handleErrors(completeResponse);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      onNext={handleSubmit}
      size="xxl"
      customContentContainer={styles.fullPageModal}
      showModalHeader
      showModalFooter
      title="Submit Final Plans"
      description={
        projectName
          ? `Upload the final construction plans for ${projectName} and complete the project.`
          : "Upload the final construction plans and complete the project."
      }
      actionButtonText={
        submitting ? "Submitting..." : "Submit Final Plans & Complete Project"
      }
      cancelButtonText="Cancel"
      cancelButtonDisabled={submitting}
      actionButtonLoading={submitting}
      actionButtonDisabled={
        submitting ||
        uploadingFiles ||
        !showUploadSuccess ||
        uploadedFileIds.length === 0 ||
        !title.trim() ||
        !isChecked
      }
      showCloseIcon={!submitting}
    >
      <div className={classNames(styles.contentBody, "flex flex-col gap-5")}>
        <div className={styles.warningBox}>
          <p className={styles.warningTitle}>Important</p>
          <p className={styles.warningText}>{WARNING_TEXT}</p>
        </div>

        <div className="w-full">
          <CustomInput
            label="Revision Title"
            placeholder="Enter revision title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
            required
          />
        </div>

        <div className="grid md:grid-cols-2 grid-cols-1 md:gap-6 gap-4">
          <div className="flex flex-col gap-2">
            <CustomTextEditor
              label="Designer Note"
              value={designerNote}
              onChange={setDesignerNote}
              placeholder="Type your message here..."
            />
            <p className={styles.noteHint}>
              Visible to the client with these final plans.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <CustomTextEditor
              label="Internal Note"
              value={internalNote}
              onChange={setInternalNote}
              placeholder="Type your message here..."
            />
            <p className={styles.noteHint}>Visible to the team only.</p>
          </div>
        </div>

        <FileUploadSection
          accept={FINAL_PLANS_DROPZONE_ACCEPT}
          uploadKey={0}
          uploadingFiles={uploadingFiles}
          uploadProgress={uploadProgress}
          showUploadSuccess={showUploadSuccess}
          currentFiles={currentFiles}
          fileIdMap={fileIdMap}
          uploadedFileIds={uploadedFileIds}
          processingFilesRef={processingFilesRef}
          getFileKey={getFileKey}
          handleFilesSelected={handleFilesSelected}
          setUploadedFileIds={setUploadedFileIds}
          setCurrentFiles={setCurrentFiles}
          setFileIdMap={setFileIdMap}
          setShowUploadSuccess={setShowUploadSuccess}
          mediaSort={mediaSort}
          onMediaSortChange={setMediaSort}
        />

        <CustomCheckbox
          labelClass={styles.checkLabel}
          label="I have read the notice above and confirm the final payment has been made. Submit the final plans and complete this project."
          checked={isChecked}
          onChange={() => setIsChecked((prev) => !prev)}
          filtersCheckbox={styles.customCheckbox}
        />
      </div>
    </CustomModal>
  );
};

export default FinalPlansModal;
